import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { IMAGES, PAGES } from '../mock';
import AccessGate from '../components/AccessGate';
import EditableImage from '../components/EditableImage';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import PhotoFrame from '../components/PhotoFrame';
import { FolderOpen } from 'lucide-react';

const imgKeyFor = (i) => `gallery-${i}`;

// Album cards from the members' gallery (shown when the visitor has access; otherwise a sign-in teaser).
function MemberAlbums() {
  const { user, listPhotoAlbums, mediaUrl } = useAuth();
  const [albums, setAlbums] = useState(null);
  const [denied, setDenied] = useState(false);
  useEffect(() => { listPhotoAlbums().then(setAlbums).catch((e) => { setAlbums([]); setDenied(e?.response?.status === 403); }); }, [listPhotoAlbums, user]);
  if (albums === null) return null;
  if (denied || albums.length === 0) {
    return denied ? <p className="text-sm mt-8 px-4 py-3 rounded" style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--olive)' }} data-testid="gallery-albums-teaser">Members' photo albums are available to signed-in members — <Link to="/login" className="underline" style={{ color: 'var(--maroon)' }}>sign in</Link> to browse them.</p> : null;
  }
  return (
    <section className="mt-10" data-testid="public-gallery-albums">
      <h2 className="font-title text-lg flex items-center gap-2 mb-3" style={{ color: 'var(--navy)' }}><FolderOpen size={18} /> Members' albums</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {albums.map((a) => (
          <Link key={a.name} to={`/members/photos/album/${encodeURIComponent(a.name)}`} className="group block rounded-lg overflow-hidden shadow-sm bg-white" style={{ border: '1px solid rgba(0,0,0,0.08)' }} data-testid={`public-album-card-${a.name}`}>
            <PhotoFrame src={mediaUrl(a.cover_url)} display={a.cover_display} alt={a.name} className="w-full h-32" style={{ background: 'rgba(0,0,0,0.04)' }} imgClassName="transition-transform duration-300 group-hover:scale-105" />
            <div className="px-3 py-2"><div className="font-title text-sm truncate" style={{ color: 'var(--maroon)' }}>{a.name}</div><div className="text-xs" style={{ color: 'var(--olive)' }}>{a.count} photo{a.count !== 1 ? 's' : ''}</div></div>
          </Link>
        ))}
      </div>
    </section>
  );
}
function useEff() {
  const { settings } = useAuth();
  return (i) => (settings && settings.images && settings.images[imgKeyFor(i)]) || GALLERY[i].src;
}

const GALLERY = [
  { src: IMAGES.groupPhoto, caption: '12 Field Squadron RAE on parade' },
  { src: IMAGES.armyTent, caption: 'Field engineering operations' },
  { src: IMAGES.bulldozer, caption: 'Plant & heavy engineering equipment' },
  { src: IMAGES.parade, caption: 'Ceremonial parade' },
  { src: IMAGES.formation, caption: 'Squadron in formation' },
  { src: IMAGES.vessel, caption: 'Watermanship & bridging' },
  { src: IMAGES.mariaIsland, caption: 'Maria Island training grounds' },
  { src: IMAGES.memorial, caption: 'Remembrance \u2014 Lest We Forget' },
];

function Lightbox({ index, onClose, onPrev, onNext }) {
  const eff = useEff();
  if (index === null) return null;
  const item = GALLERY[index];
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
    >
      <button className="absolute top-5 right-5 text-white" onClick={onClose}><X size={30} /></button>
      <button className="absolute left-5 text-white" onClick={(e) => { e.stopPropagation(); onPrev(); }}><ChevronLeft size={40} /></button>
      <div onClick={(e) => e.stopPropagation()} className="max-w-3xl text-center">
        <img src={eff(index)} alt={item.caption} className="max-h-[75vh] w-auto mx-auto rounded shadow-2xl" />
        <p className="text-white font-title mt-3">{item.caption}</p>
      </div>
      <button className="absolute right-5 text-white" onClick={(e) => { e.stopPropagation(); onNext(); }}><ChevronRight size={40} /></button>
    </div>
  );
}

export default function Gallery({ pageKey, mode = 'grid' }) {
  const data = PAGES[pageKey];
  const eff = useEff();
  const [lightbox, setLightbox] = useState(null);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (mode !== 'slideshow') return undefined;
    const t = setInterval(() => setSlide((s) => (s + 1) % GALLERY.length), 3500);
    return () => clearInterval(t);
  }, [mode]);

  return (
    <AccessGate pageKey={pageKey}>
      <div className="max-w-5xl mx-auto px-4 py-10 fade-in">
        <h1 className="font-title text-3xl md:text-4xl mb-2" style={{ color: 'var(--maroon)' }}>{data.title}</h1>
        <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />
        {data.intro && <p className="mb-6 text-[15px]">{data.intro}</p>}

        {mode === 'slideshow' ? (
          <div className="relative rounded-lg overflow-hidden shadow-lg" style={{ border: '4px solid white' }}>
            <img src={eff(slide)} alt={GALLERY[slide].caption} className="w-full h-[420px] object-cover" />
            <div className="absolute bottom-0 left-0 right-0 px-4 py-3 font-title" style={{ background: 'rgba(21,56,91,0.85)', color: 'var(--cream)' }}>
              {GALLERY[slide].caption}
            </div>
            <div className="absolute bottom-3 right-4 flex gap-1.5">
              {GALLERY.map((_, i) => (
                <button key={i} onClick={() => setSlide(i)} className="w-2.5 h-2.5 rounded-full" style={{ background: i === slide ? 'var(--gold)' : 'rgba(255,255,255,0.5)' }} />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {GALLERY.map((g, i) => (
              <button
                key={i}
                onClick={() => setLightbox(i)}
                className="group relative overflow-hidden rounded shadow-sm"
                style={{ border: '3px solid white' }}
              >
                <EditableImage imgKey={imgKeyFor(i)} src={g.src} alt={g.caption} className="w-full h-44 object-cover transition-transform duration-300 group-hover:scale-110" />
                <span className="absolute inset-x-0 bottom-0 px-2 py-1.5 text-xs text-left opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(21,56,91,0.85)', color: 'var(--cream)' }}>
                  {g.caption}
                </span>
              </button>
            ))}
          </div>
        )}
        <MemberAlbums />
      </div>
      <Lightbox
        index={lightbox}
        onClose={() => setLightbox(null)}
        onPrev={() => setLightbox((i) => (i - 1 + GALLERY.length) % GALLERY.length)}
        onNext={() => setLightbox((i) => (i + 1) % GALLERY.length)}
      />
    </AccessGate>
  );
}
